import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import Fields from './Fields';
import Information from './Information';
import Dispositions from './Dispositions';
import { useUserDetails } from '../../store/slices/authSlice';
import { setCallingModalOpen, setSidebarType, useTimezone } from '../../store/slices/commonSlice';
import {
  crmInfoLog,
  followUpAdd,
  handleConference,
  hangupCall,
  pauseModeUpdate,
  // removeCrmDetails,
  setPauseMode,
  updateCrmInformation,
  useCrmDetails
} from '../../store/slices/callingSlice';
import {
  onSetCallStatus,
  onSetCallType,
  setCallDialSec,
  setCallEnd,
  setCallId,
  setConferenceDetail,
  setCurrentCallDetail,
  setDialNumberType,
  setHoldStartTime,
  setHoldTime,
  setIsDialNumber,
  useCallEnd,
  useCallId,
  useCallingStatus,
  useCallType,
  useConferenceDetail,
  useCurrentCallDetail,
  useDialSec,
  useHoldStartTime,
  useHoldTime,
  useIsReDial
} from '../../store/slices/dialSlice';
import { useCallingData } from '../../components/Context/CallingContext';
import { format, isToday, setHours, setMinutes, setSeconds, startOfDay } from 'date-fns';
import FormSelect from '../../components/form/FormSelect';
import { yesNoOptions } from '../../utils/common';
import { IS_CRM_COMPACT } from '../../utils/crmLayout';
import { isOnBreakNow } from '../../utils/breakState';
import { fetchCrmAndLand } from '../../utils/crmLanding';
import { getMediaLiveness } from '../../utils/mediaLiveness';
import FormDateInput from '../../components/form/FormDateInput';
import { toZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const Crm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const timezone = useTimezone();
  const today = toZonedTime(new Date(), timezone);
  const crmDetails = useCrmDetails();
  const CurrentCallDetail = useCurrentCallDetail();
  const dialSec = useDialSec();
  const callStatus = useCallingStatus();
  const user = useUserDetails();
  const callId = useCallId();
  const callEnd = useCallEnd();
  const isReDial = useIsReDial();
  const holdTime = useHoldTime();
  const holdStartTime = useHoldStartTime();
  const callType = useCallType();
  const conferenceDetail = useConferenceDetail();

  const {
    informationData,
    dispositionsData,
    fieldsData,
    followUp,
    isPauseMode,
    setInformationData,
    setDispositionsData,
    setFieldsData,
    setFollowUp,
    setIsPauseMode,
    handleTimeReset,
    setMutedConference,
    setOnHoldConference
  } = useCallingData();

  // const [wrapsec, setWrapsec] = useState(0);
  const wrapsecRef = useRef(0);
  const onUpdateCrmRef = useRef();
  const autoSubmittedRef = useRef(false);
  const pendingDialRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);

  // useEffect(() => {
  //   let timerInterval;
  //   if (callStatus === 'WRAPUP') {
  //     timerInterval = setInterval(() => {
  //       setWrapsec((prevTime) => prevTime + 1);
  //     }, 1000);
  //   }
  //   return () => {
  //     clearInterval(timerInterval);
  //   };
  // }, [callStatus]);

  useEffect(() => {
    if (callStatus !== 'WRAPUP') return;

    wrapsecRef.current = 0; // reset when wrapup starts

    const interval = setInterval(() => {
      wrapsecRef.current += 1;
    }, 1000);

    return () => clearInterval(interval);
  }, [callStatus]);

  // useEffect(() => {
  //   const autoWrapUpZero = String(user?.process_details?.autoWrapup) === '0';
  //   const autoWrapup = Number(user?.process_details?.autoWrapup);
  //   if (((!autoWrapUpZero && wrapsecRef.current > autoWrapup) || callEnd) && !isLoading) {
  //     onUpdateCrm();
  //   }
  // }, [wrapsecRef.current, callEnd]);

  useEffect(() => {
    if (process.env.REACT_APP_MANUAL_CALL_HANGUP === 'true') return;

    // const autoWrapUpZero = String(user?.process_details?.autoWrapup) === '0';
    const autoWrapup = Number(user?.process_details?.autoWrapup) || 10;

    // if (callStatus !== 'WRAPUP' || autoWrapUpZero) return;
    if (callStatus !== 'WRAPUP') return;

    autoSubmittedRef.current = false;

    const interval = setInterval(() => {
      if (autoWrapup > 10 && autoWrapup - wrapsecRef.current === 5) {
        const dispoRequired = user?.process_details?.is_dispo_required === 'yes';
        const subDispoRequired = user?.process_details?.is_sub_dispo_required === 'yes';
        const subSubDispoRequired = user?.process_details?.is_sub_sub_dispo_required === 'yes';

        const needsDispo = dispoRequired && !dispositionsData?.dispositioncode;
        const needsSubDispo = subDispoRequired && !dispositionsData?.subdispositioncode;
        const needsSubSubDispo = subSubDispoRequired && !dispositionsData?.subsubdispositioncode;

        if (needsDispo || needsSubDispo || needsSubSubDispo) {
          toast.warning('Please fill the required disposition fields. 5 seconds remaining.');
        }
      }
      // Never auto-submit a CRM for an agent on break: it dispositions a call they
      // are no longer on and pushes them back through the post-call flow.
      //
      // Nor while the media session is still Established. During a live call the agent
      // is normally sitting on this route — the sidebar's own auto-submit is gated on
      // `pathname !== '/crm'` — so THIS is the timer that fires when a stray socket
      // event flips a live call to WRAPUP, and it is what writes an AUTOWRAP
      // disposition over a conversation still in progress. 'unknown' (external phone,
      // no browser session) must not block, or those agents would never auto-wrap.
      if (
        wrapsecRef.current >= autoWrapup &&
        !autoSubmittedRef.current &&
        !isLoading &&
        !isOnBreakNow() &&
        getMediaLiveness() !== 'live'
      ) {
        autoSubmittedRef.current = true;
        onUpdateCrmRef.current(true);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [callStatus]);

  const onHangUpCall = async () => {
    try {
      const payload = {
        call_uuid: callId
      };
      await dispatch(hangupCall(payload)).unwrap();
      updateCrm();
    } catch (error) {
      pendingDialRef.current = null;
      console.log('error', error);
    }
  };

  const onPauseModeChange = async () => {
    try {
      const payload = { user_id: user?.id, pause_mode: 1 };
      const response = await dispatch(pauseModeUpdate(payload)).unwrap();
      if (response && response?.success) {
        dispatch(setPauseMode(payload?.pause_mode));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  // Owns the "already on /crm but details are missing" case: crmDetails is cleared
  // at every new call's start, so this refetches for the CURRENT call only. Landing
  // (retries, break guards, embedded route) is the shared helper's job.
  useEffect(() => {
    // `currentCallDetail` is persisted, so without this check the CRM re-opens for a
    // stale call — during a break, and again after a reload taken while on break.
    if (isOnBreakNow()) return;
    if (!crmDetails && CurrentCallDetail && CurrentCallDetail?.unique_id) {
      fetchCrmAndLand({ dispatch, navigate, data: CurrentCallDetail });
    }
  }, [crmDetails, callId]);

  const getLogDetails = async (data) => {
    try {
      await dispatch(crmInfoLog(data)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const updateCrm = async (isAuto = false) => {
    if (conferenceDetail && conferenceDetail?.details?.length > 0) {
      handleConferenceMode();
    }
    try {
      const dispoRequired = user?.process_details?.is_dispo_required === 'yes';
      const subDispoRequired = user?.process_details?.is_sub_dispo_required === 'yes';
      const subSubDispoRequired = user?.process_details?.is_sub_sub_dispo_required === 'yes';

      if (!isAuto && !isReDial) {
        if (dispoRequired && !dispositionsData?.dispositioncode) {
          toast.error('Please select a disposition');
          setIsLoading(false);
          dispatch(setCallEnd(false));
          pendingDialRef.current = null;
          return;
        }
        if (subDispoRequired && !dispositionsData?.subdispositioncode) {
          toast.error('Please select a sub disposition');
          setIsLoading(false);
          dispatch(setCallEnd(false));
          pendingDialRef.current = null;
          return;
        }
        if (subSubDispoRequired && !dispositionsData?.subsubdispositioncode) {
          toast.error('Please select a sub-sub disposition');
          setIsLoading(false);
          dispatch(setCallEnd(false));
          pendingDialRef.current = null;
          return;
        }
      }

      // const autoWrapUpZero = String(user?.process_details?.autoWrapup) === '0';

      const autoWrapup = Number(user?.process_details?.autoWrapup) || 10;

      let hold_time = 0;

      if (holdStartTime !== null) {
        const holdDuration = Math.floor((Date.now() - holdStartTime) / 1000);
        hold_time = holdTime + holdDuration;
      } else {
        hold_time = holdTime;
      }

      const payload = {
        data: [
          {
            crmDetails: { ...informationData }
          },
          {
            fields: fieldsData?.fields ? fieldsData?.fields : []
          },
          {
            dispositions: dispositionsData?.dispositioncode
              ? { ...dispositionsData }
              : { dispositioncode: 'AUTOWRAP' }
          },
          {
            unique_id: crmDetails?.[4]?.unique_id,
            dialsec: callType === 'predictive' || callType === 'incoming' ? 0 : dialSec,
            wrapsec: wrapsecRef.current,
            hold_time: hold_time,
            // auto_wrapup: !autoWrapUpZero ? autoWrapup <= wrapsecRef.current : false
            auto_wrapup: autoWrapup <= wrapsecRef.current
          }
        ]
      };
      if (followUp?.isFollowUp === 'yes') {
        dispatch(
          followUpAdd({
            phone_number: crmDetails?.[1]?.crmDetails?.primaryNumber,
            crm_id: crmDetails?.[1]?.crmDetails?.Crms_id,
            date_and_time: format(new Date(followUp?.date_and_time), 'yyyy-MM-dd HH:mm:ss')
          })
        ).unwrap();
      }

      if (isPauseMode) {
        onPauseModeChange();
      }

      // TEMP DIAGNOSTIC (safe to remove) — confirms whether freshly added/edited CRM
      // data actually reaches the payload on Redial. Reproduce: add data -> click Redial,
      // then compare this log's `isReDial:true` entry against a normal Submit entry for
      // the same record to see exactly which slice (comment / fields) is missing.
      console.log('[CRM UPDATE PAYLOAD]', {
        isReDial,
        comment: informationData?.comment,
        informationData,
        fields: fieldsData?.fields,
        payload
      });

      const data_message = {
        user_id: user.user_id,
        payload: payload,
        message: 'update Crm Information'
      };
      getLogDetails(data_message);
      const response = await dispatch(updateCrmInformation(payload)).unwrap();
      if (response) {
        const REDIAL_DELAY_MS = 4000;
        const NORMAL_DELAY_MS = 500;
        const postUpdateDelay = isReDial ? REDIAL_DELAY_MS : NORMAL_DELAY_MS;

        const responseTimer = setTimeout(() => {
          if (isReDial) {
            dispatch(setIsDialNumber(crmDetails?.[1]?.crmDetails?.primaryNumber));
          }
          dispatch(setCallId(null));
          dispatch(setCallingModalOpen(user?.manualcall ? true : false));
          dispatch(setCallDialSec(null));
          dispatch(setSidebarType('main'));
          dispatch(setCallEnd(false));
          dispatch(onSetCallStatus(null));
          handleTimeReset();
          dispatch(setCurrentCallDetail(null));
          dispatch(onSetCallType(null));
          if (location.pathname !== '/chat') {
            navigate('/dashboard');
          }
          setIsLoading(false);
          dispatch(setHoldTime(0));
          dispatch(setHoldStartTime(null));
          setFollowUp({
            date_and_time: startOfDay(today),
            isFollowUp: 'no'
          });
          setIsPauseMode(false);

          const pending = pendingDialRef.current;
          pendingDialRef.current = null;
          if (pending?.number) {
            dispatch(setDialNumberType(pending.numberType || null));
            dispatch(setIsDialNumber(pending.number));
          }
        }, postUpdateDelay);

        // const removeTimer = setTimeout(() => {
        //   dispatch(removeCrmDetails());
        // }, 600);

        return () => {
          clearTimeout(responseTimer);
          // clearTimeout(removeTimer);
        };
      }
    } catch (error) {
      setIsLoading(false);
      pendingDialRef.current = null;
      const data_message = {
        user_id: user.user_id,
        payload: error?.message,
        message: 'update Crm Information error'
      };
      getLogDetails(data_message);
      console.log('error', error);
    }
  };

  const handleConferenceMode = async () => {
    try {
      const payload = {
        conf_id: 'all',
        type: 'hangup',
        conf_name: conferenceDetail?.conference_name
      };
      await dispatch(handleConference(payload)).unwrap();
      dispatch(setConferenceDetail(null));
      setMutedConference([]);
      setOnHoldConference([]);
    } catch (error) {
      console.log('error', error);
    }
  };

  const onUpdateCrm = async (isAuto = false) => {
    setIsLoading(true);
    if (callStatus !== 'WRAPUP') {
      onHangUpCall();
    } else {
      updateCrm(isAuto);
    }
  };

  const handleSubmitWithDial = ({ pendingDial } = {}) => {
    pendingDialRef.current = pendingDial || null;
    onUpdateCrm();
  };

  onUpdateCrmRef.current = onUpdateCrm;

  useEffect(() => {
    if (process.env.REACT_APP_MANUAL_CALL_HANGUP === 'true') return;
    if (!callEnd) return;
    onUpdateCrm();
  }, [callEnd]);

  return (
    <Card className={`d-flex flex-column m-0${IS_CRM_COMPACT ? ' crm-compact' : ''}`}>
      <CardTitle className="px-4 py-2">
        <span className="fs-4 fw-bolder">
          {crmDetails?.[0]?.main_crms?.crm_name} {t('information')}
        </span>
      </CardTitle>
      <CardBody style={{ height: 'calc(100vh - 215px)', overflowY: 'auto' }}>
        <Information
          data={crmDetails?.[1]?.crmDetails}
          onUpdate={setInformationData}
          onSubmit={handleSubmitWithDial}
        />
        <Dispositions data={crmDetails?.[3]?.disposition} onUpdate={setDispositionsData} />
        <Fields
          data={crmDetails?.[2]?.fields}
          onUpdate={setFieldsData}
          followUp={followUp}
          setFollowUp={setFollowUp}
        />
      </CardBody>
      <CardFooter className="text-start d-flex align-items-end gap-4 w-100">
        <Button
          color="danger"
          className="d-flex justify-content-center align-items-center gap-2 text-nowrap"
          onClick={() => onUpdateCrm()}
          disabled={isLoading}>
          {callStatus === 'WRAPUP' ? t('submit') : t('endCall')}
          {isLoading ? <Spinner size="sm" color="light" /> : null}
        </Button>
        <Row className="w-100">
          <Col lg="4" md="4" className="flex-row">
            <FormSelect
              name="isFollowUp"
              value={followUp?.isFollowUp}
              label={t('followUp')}
              className="callex-mb-0"
              options={yesNoOptions}
              onChange={(e) =>
                setFollowUp({
                  ...followUp,
                  isFollowUp: e.target.value,
                  date_and_time: today
                })
              }
            />
          </Col>
          {followUp?.isFollowUp === 'yes' ? (
            <Col lg="4" md="4" className="flex-row">
              <FormDateInput
                name="date_and_time"
                dateFormat="yyyy-MM-dd HH:mm:ss"
                label={t('fromDate')}
                className="follow-up-datepicker callex-mb-0"
                showTimeSelect={true}
                value={followUp.date_and_time}
                minDate={today}
                timeIntervals="5"
                minTime={
                  isToday(followUp.date_and_time)
                    ? today
                    : setHours(setMinutes(setSeconds(today, 0), 0), 0)
                }
                maxTime={setHours(setMinutes(setSeconds(today, 59), 59), 23)}
                onChange={(e) => setFollowUp({ ...followUp, date_and_time: e.target.value })}
              />
            </Col>
          ) : null}
          {process.env.REACT_APP_AGENT_PAUSE_MODE === 'true' &&
            (user?.process_details?.type === 'OutboundAndPredictive' ||
              user?.process_details?.type === 'BlendedAndPredictive') ? (
            <Col lg="4" md="4" className="d-flex justify-content-end flex-column">
              <div className="me-2 d-flex">
                <FormGroup check className="m-0">
                  <Label check>
                    <Input
                      type="checkbox"
                      id="isPauseMode"
                      checked={isPauseMode}
                      disabled={isLoading}
                      onChange={() => setIsPauseMode(!isPauseMode)}
                    />
                  </Label>
                </FormGroup>
                <div className="text-dark fw-bold">{t('pauseCall')}</div>
              </div>
            </Col>
          ) : null}
        </Row>
      </CardFooter>
    </Card>
  );
};

export default Crm;
